/**
 * Propriétés pour les conditions (sur une flèche après une Gateway)
 * Cela va être enregistré dans le BPMN sous la forme :
 * <bpmn:sequenceFlow id="a" sourceRef="b" targetRef="c" 
 *  aktor:type="form"
 *  aktor:field="field1"
 *  aktor:condition="hasValue"
 *  aktor:value="001" />
 * 
 * aktor:type :
 * - '' pour une absence de condition => toujours vrai
 * - 'form' pour une condition sur un champ de formulaire rempli par le contact
 * - 'contact' pour une condition sur un champ deu contact enregistré dans la liste de diffusion
 * - 'else' pour une condition par défaut (si aucune des conditions précédentes n'est remplie)
 * 
 * aktor:field :
 * - le nom du champ de formulaire ou le nom du champ de contact
 * 
 * aktor:condition :
 * - 'hasValue' : le champ a une valeur
 * - 'contains' : le champ contient la valeur
 * - 'startsWith' : le champ commence par la valeur
 * - 'isEmpty' : le champ est vide
 * - 'isNotEmpty' : le champ n'est pas vide
 * 
 * aktor:value :
 * - la valeur à vérifier. Elle est présentée sous forme d'un champ texte ou d'un champ select,
 *   en fonction du type du champ choisi. Si select, c'est la valeur interne qui est enregistrée.
 */
import { html } from 'htm/preact';
import { SelectEntry, isSelectEntryEdited, TextFieldEntry, isTextFieldEntryEdited } from '@bpmn-io/properties-panel';
import { useService } from 'bpmn-js-properties-panel';
import { extractBestTranslation } from '@/utils/helpers';

function ConditionProps(element) {
  // console.log(element);
  return [{
    id: 'type',
    element,
    component: TypeField,
    isEdited: isSelectEntryEdited
  }, {
    id: 'formField',
    element,
    component: FormFieldField,
    isEdited: isSelectEntryEdited
  }, {
    id: 'contactField',
    element,
    component: ContactFieldField,
    isEdited: isSelectEntryEdited
  }, {
    id: 'condition',
    element,
    component: FormConditionField,
    isEdited: isSelectEntryEdited
  }, {
    id: 'valueText',
    element,
    component: TextValueField,
    isEdited: isTextFieldEntryEdited
  }, {
    id: 'valueList',
    element,
    component: ListValueField,
    isEdited: isTextFieldEntryEdited
  }];
}

// Liste de toutes les conditions possibles, selon le type de champ
const allConditions = [
  {
    label: 'has value...',
    key: 'hasValue',
    fieldTypes: ['text', 'textarea'],
    valueType: 'text'
  },
  {
    label: 'contains...',
    key: 'contains',
    fieldTypes: ['text', 'textarea'],
    valueType: 'text'
  },
  {
    label: 'starts with...',
    key: 'startsWith',
    fieldTypes: ['text', 'textarea'],
    valueType: 'text'
  },
  {
    label: 'has value...',
    key: 'hasValue',
    fieldTypes: ['select', 'selectmultiple', 'radios', 'checkboxes'],
    valueType: 'list'
  },
  {
    label: 'is empty',
    key: 'isEmpty',
    fieldTypes: ['text', 'textarea', 'select', 'selectmultiple', 'radios', 'checkboxes', 'date', 'datetime'],
    valueType: 'none'
  },
  {
    label: 'is not empty',
    key: 'isNotEmpty',
    fieldTypes: ['text', 'textarea', 'select', 'selectmultiple', 'radios', 'checkboxes', 'date', 'datetime'],
    valueType: 'none'
  },
  {
    label: 'is checked',
    key: 'isNotEmpty',
    fieldTypes: ['checkbox', 'switch'],
    valueType: 'none'
  },
  {
    label: 'is not checked',
    key: 'isEmpty',
    fieldTypes: ['checkbox', 'switch'],
    valueType: 'none'
  }
];

function getAllFormFields(bpmnjs) {
  // Récupérer tous les sendTask du diagramme
  const elementRegistry = bpmnjs.get('elementRegistry');
  const sendTasks = elementRegistry.filter(element => element.type === 'bpmn:SendTask');

  // Collecter tous les champs des formulaires
  let allFields = [];
  sendTasks.forEach(task => {
    if (task.businessObject['send-form'] === 'self') {
      try {
        const formJson = JSON.parse(task.businessObject['form-json']);
        allFields = [
          ...allFields,
          ...formJson.fields.filter(field => field.type !== 'info')
        ];
      } catch (e) {
        console.warn('Erreur lors du parsing du form-json:', e);
      }
    }
  });

  return allFields;
}

function TypeField({ element, id }) {

  const modeling = useService('modeling');
  const translate = useService('translate');
  const debounce = useService('debounceInput');

  const getValue = () => {
    return element.businessObject['type'] || '';
  };

  const setValue = (value) => {
    let result = {};
    result['type'] = value;
    return modeling.updateProperties(element, result);
  };

  const getOptions = () => {
    return [
      { value: '', label: '' },
      { value: 'form', label: translate('If the form field...') },
      { value: 'contact', label: translate('If the contact field...') },
      { value: 'else', label: translate('Otherwise (if no other condition has been met)') }
    ];
  };

  return html`<${SelectEntry}
    id=${id}
    element=${element}
    label=""
    getValue=${getValue}
    setValue=${setValue}
    getOptions=${getOptions}
    debounce=${debounce}
  />`;
}

function FormFieldField({ element, id }) {

  const modeling = useService('modeling');
  const translate = useService('translate');
  const debounce = useService('debounceInput');
  const bpmnjs = useService('bpmnjs');

  const getValue = () => {
    return element.businessObject['field'] || '';
  };

  const setValue = (value) => {
    let result = {};
    result['field'] = value;
    return modeling.updateProperties(element, result);
  };

  const isVisible = () => {
    return element.businessObject['type'] === 'form';
  };

  const getOptions = () => {
    const fields = getAllFormFields(bpmnjs);
    return fields.map(field => ({
      value: field.name,
      label: extractBestTranslation(field.label, translate.currentLanguage) || field.name
    }));
  };

  return isVisible() ? html`<${SelectEntry}
    id=${id}
    element=${element}
    label=${translate('Field')}
    getValue=${getValue}
    setValue=${setValue}
    getOptions=${getOptions}
    debounce=${debounce}
  />` : '';
}

function ContactFieldField({ element, id }) {

  const modeling = useService('modeling');
  const translate = useService('translate');
  const debounce = useService('debounceInput');

  const getValue = () => {
    return element.businessObject['field'] || '';
  };

  const setValue = (value) => {
    let result = {};
    result['field'] = value;
    return modeling.updateProperties(element, result);
  };

  const isVisible = () => {
    return element.businessObject['type'] === 'contact';
  };

  return isVisible() ? html`<${TextFieldEntry}
    id=${id}
    element=${element}
    label=${translate('Field')}
    getValue=${getValue}
    setValue=${setValue}
    debounce=${debounce}
    tooltip=${translate('contact-field-tooltip').replace('{{list}}', '"gender", "first_name", "last_name", "email", "phone", "lang"')}
  />` : '';
}

function FormConditionField({ element, id }) {

  const modeling = useService('modeling');
  const translate = useService('translate');
  const debounce = useService('debounceInput');
  const bpmnjs = useService('bpmnjs');
  const getValue = () => {
    return element.businessObject['condition'] || '';
  };

  const setValue = (value) => {
    let result = {};
    result['condition'] = value;
    return modeling.updateProperties(element, result);
  };

  const isVisible = () => {
    return element.businessObject['type'] === 'contact'
      || (
        element.businessObject['type'] === 'form'
        && element.businessObject['field'] !== undefined
      );
  };

  const getOptions = () => {
    let fieldType = '';
    if (element.businessObject['type'] === 'contact') {
      fieldType = 'text';
    } else {
      const fieldName = element.businessObject['field'];
      const fields = getAllFormFields(bpmnjs);
      const field = fields.find(f => f.name === fieldName);
      fieldType = field ? field.type : '';
    }
    const conditions = allConditions.filter(condition => condition.fieldTypes.includes(fieldType));
    return conditions.map(condition => ({
      value: condition.key,
      label: translate(condition.label)
    }));
  };

  return isVisible() ? html`<${SelectEntry}
    id=${id}
    element=${element}
    label=${translate('Condition')}
    getValue=${getValue}
    setValue=${setValue}
    getOptions=${getOptions}
    debounce=${debounce}
  />` : '';
}

function TextValueField({ element, id }) {

  const modeling = useService('modeling');
  const translate = useService('translate');
  const debounce = useService('debounceInput');
  const bpmnjs = useService('bpmnjs');
  const getValue = () => {
    return element.businessObject['value'] || '';
  };

  const setValue = (value) => {
    let result = {};
    result['value'] = value;
    return modeling.updateProperties(element, result);
  };

  const isVisible = () => {

    const currentCondition = element.businessObject['condition'];

    switch (element.businessObject['type']) {
      case 'contact':
        const possibleConditions = allConditions.filter(condition => condition.fieldTypes.includes('text'));
        const currentConditionDetails = possibleConditions.find(condition => condition.key === currentCondition);
        const result = currentConditionDetails && currentConditionDetails.valueType === 'text';
        return result;
      case 'form':
        const currentFieldName = element.businessObject['field'];
        if (currentFieldName !== undefined && currentCondition !== undefined) {
          const currentField = getAllFormFields(bpmnjs).find(f => f.name === currentFieldName);
          const currentFieldType = currentField ? currentField.type : '';
          const possibleConditions = allConditions.filter(condition => condition.fieldTypes.includes(currentFieldType));
          const currentConditionDetails = possibleConditions.find(condition => condition.key === currentCondition);
          const result = currentConditionDetails && currentConditionDetails.valueType === 'text';
          return result;
        }
        return false;
      default:
        return false;
    }
  }

  return isVisible() ? html`<${TextFieldEntry}
    id=${id}
    element=${element}
    label=${translate('Value')}
    getValue=${getValue}
    setValue=${setValue}
    debounce=${debounce}
  />` : '';
}

function ListValueField({ element, id }) {

  const modeling = useService('modeling');
  const translate = useService('translate');
  const debounce = useService('debounceInput');
  const bpmnjs = useService('bpmnjs');

  const getValue = () => {
    return element.businessObject['value'] || '';
  };

  const setValue = (value) => {
    let result = {};
    result['value'] = value;
    return modeling.updateProperties(element, result);
  };

  const getOptions = () => {
    const currentFieldName = element.businessObject['field'];
    const currentField = getAllFormFields(bpmnjs).find(f => f.name === currentFieldName);
    const currentFieldEnum = currentField ? currentField.enum : {};
    const options = Object.keys(currentFieldEnum).map(key => ({
      value: key,
      label: extractBestTranslation(currentFieldEnum[key], translate.currentLanguage)
    }));
    return options;
  };

  const isVisible = () => {

    switch (element.businessObject['type']) {
      case 'contact':
        return false;
      case 'form':
        const currentFieldName = element.businessObject['field'];
        const currentCondition = element.businessObject['condition'];
        if (currentFieldName !== undefined && currentCondition !== undefined) {
          const currentField = getAllFormFields(bpmnjs).find(f => f.name === currentFieldName);
          const currentFieldType = currentField ? currentField.type : '';
          const possibleConditions = allConditions.filter(condition => condition.fieldTypes.includes(currentFieldType));
          const currentConditionDetails = possibleConditions.find(condition => condition.key === currentCondition);
          const result = currentConditionDetails && currentConditionDetails.valueType === 'list';
          return result;
        }
        return false;
      default:
        return false;
    }
  }

  return isVisible() ? html`<${SelectEntry}
    id=${id}
    element=${element}
    label=${translate('Value')}
    getValue=${getValue}
    setValue=${setValue}
    getOptions=${getOptions}
    debounce=${debounce}
  />` : '';
}

export default ConditionProps;